import { forwardRef, memo, RefObject, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { gsap } from 'gsap';

import Button from '@/shared/components/button/Button';

interface WordProgressProps {
	controlBarRef: RefObject<HTMLDivElement | null>;
	currentWord: string;
	revealedLetters: string;
	onSpeak: (word: string, forceSpeak?: boolean) => void;
	isComplete: boolean;
}

export interface WordProgressHandle {
	getLetterBoxRect: (index: number) => DOMRect | null;
}

const WordProgress = forwardRef<WordProgressHandle, WordProgressProps>(
	({ controlBarRef, currentWord, revealedLetters, onSpeak, isComplete }, ref) => {
		const letters = useMemo(() => currentWord.trim().split('').filter(Boolean), [currentWord]);
		const revealedCount = Math.min(revealedLetters.length, letters.length);
		const letterRefs = useRef<Array<HTMLDivElement | null>>([]);
		const containerRef = useRef<HTMLDivElement | null>(null);
		const completeTimelineRef = useRef<gsap.core.Timeline | null>(null);

		useEffect(() => {
			letterRefs.current = letterRefs.current.slice(0, letters.length);
		}, [letters.length]);

		useImperativeHandle(
			ref,
			() => ({
				getLetterBoxRect: (index: number) => letterRefs.current[index]?.getBoundingClientRect() ?? null,
			}),
			[],
		);

		useLayoutEffect(() => {
			const container = containerRef.current;
			const controlBar = controlBarRef.current;

			if (!container) {
				return undefined;
			}

			if (completeTimelineRef.current) {
				completeTimelineRef.current.kill();
				completeTimelineRef.current = null;
			}

			if (!isComplete) {
				gsap.set(container, { clearProps: 'all' });
				return undefined;
			}

			if (typeof window === 'undefined') {
				return undefined;
			}

			const rect = container.getBoundingClientRect();
			const originCenterX = rect.left + rect.width / 2;
			const originCenterY = rect.top + rect.height / 2;
			const viewportCenterX = window.innerWidth / 2;
			const viewportCenterY = window.innerHeight / 2;
			const deltaX = viewportCenterX - originCenterX;
			const deltaY = viewportCenterY - originCenterY;

			// Calculate dynamic scale to ensure word fits on screen
			const maxScale = 2;
			const screenPadding = 40; // Leave 20px padding on each side
			const maxAllowedWidth = window.innerWidth - screenPadding;
			const scaledWidth = rect.width * maxScale;
			const calculatedScale = scaledWidth > maxAllowedWidth ? maxAllowedWidth / rect.width : maxScale;

			const timeline = gsap.timeline();
			completeTimelineRef.current = timeline;

			timeline
				.set(controlBar, { zIndex: 10 })
				.set(container, {
					position: 'fixed',
					left: rect.left,
					top: rect.top,
					x: 0,
					y: 0,
					willChange: 'transform',
					zIndex: 400,
					transformOrigin: 'center center',
				})
				.to(container, {
					duration: 0.65,
					x: deltaX,
					y: deltaY,
					scale: calculatedScale,
					ease: 'power3.out',
				});

			return () => {
				timeline.kill();
				completeTimelineRef.current = null;
			};
		}, [isComplete]);

		const handleSpeak = () => {
			if (!currentWord) {
				return;
			}
			onSpeak(currentWord, true);
		};

		const progressClassName = classNames('letter-party-game__word-progress', {
			'letter-party-game__word-progress--complete': isComplete,
		});

		return (
			<div ref={containerRef} className={progressClassName}>
				{!isComplete && (
					<Button
						type="primary"
						color="yellow"
						rounded
						iconName="icon-volume-on"
						className="letter-party-game__speaker-button"
						onClick={handleSpeak}
					/>
				)}

				<div className="letter-party-game__letter-progress" aria-live={isComplete ? 'off' : 'polite'}>
					{letters.map((letter, idx) => {
						const isCollected = idx < revealedCount;

						return (
							<div
								key={`${letter}-${idx}`}
								ref={(node) => {
									letterRefs.current[idx] = node;
								}}
								className={classNames('letter-party-game__letter-box', {
									'letter-party-game__letter-box--collected': isCollected,
								})}
							>
								<span className="letter-party-game__letter">
									{isCollected ? letter.toLowerCase() : ''}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		);
	},
);

export default memo(WordProgress);
